import { Component, Input, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { DjiDraftPreview } from '../../../core/response-models/dji-draft-preview.model';
import { DjiDroneStatus } from '../../../core/response-models/dji-drone-status.model';
import { DjiService } from '../../../core/api-services/dji.service';
import { DjiAuthorizationGuideComponent } from '../dji-authorization-guide/dji-authorization-guide.component';
import { StatusBadgePipe } from '../../pipes/status-badge.pipe';
import {
  describeDjiDraft,
  formatDjiAcres,
  formatDjiDuration,
  formatDjiFlightStart,
  sprayRecordStatusLabel
} from '../../utils/dji-flight.utils';

/**
 * Whether ADN is receiving DJI flights for a drone, with the steps to connect
 * it (ADN-2307). "Receiving" is derived from flights actually received and
 * attributed to this organization; it does not (yet) reflect DJI's own
 * authorization setting, so an authorized drone that has not flown reads as
 * "No DJI data yet".
 */
@Component({
  selector: 'app-dji-connection-card',
  standalone: true,
  imports: [CommonModule, RouterLink, StatusBadgePipe, DjiAuthorizationGuideComponent],
  templateUrl: './dji-connection-card.component.html'
})
export class DjiConnectionCardComponent implements OnChanges {
  @Input({ required: true }) droneId!: string;
  /** The drone's name, shown in the preview of a record started from one of its flights. */
  @Input() droneName = '';

  private readonly dji = inject(DjiService);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly failed = signal(false);
  readonly status = signal<DjiDroneStatus | null>(null);
  readonly showGuide = signal(false);
  /** The flight an action is running for, so its buttons are disabled and nothing is sent twice. */
  readonly busyIntakeId = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  /** The flight whose draft is being previewed, and what it would create. */
  readonly previewIntakeId = signal<string | null>(null);
  readonly preview = signal<DjiDraftPreview | null>(null);

  readonly acres = formatDjiAcres;
  readonly duration = formatDjiDuration;
  readonly startLabel = formatDjiFlightStart;
  readonly describeDraft = describeDjiDraft;
  readonly recordStatusLabel = sprayRecordStatusLabel;

  /** Counts loads, so a slower, older response can never overwrite a newer one. */
  private loadSeq = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['droneId'] && this.droneId) {
      // A different drone: nothing of the previous one (flights, an open preview, an error) may linger.
      this.status.set(null);
      this.cancelPreview();
      this.actionError.set(null);
      void this.load();
    }
  }

  trackByIntake(_index: number, flight: { intakeId: string }): string {
    return flight.intakeId;
  }

  /** Flights received but not yet linked to a spray record, and not dismissed. */
  notLinkedCount(status: DjiDroneStatus): number {
    return Math.max(0, status.flightCount - status.linkedCount - status.dismissedCount);
  }

  /**
   * Shows what a spray record started from this flight would contain, before anything is created. A flight
   * that already has a record opens it instead.
   */
  async showPreview(flight: { intakeId: string }): Promise<void> {
    // Close any open preview first, so one for another flight can't sit there while this one loads.
    this.cancelPreview();
    await this.runAction(flight.intakeId, async () => {
      const outcome = await this.dji.previewSprayRecordFromFlight(flight.intakeId);
      if ('alreadyLinked' in outcome) {
        await this.router.navigate(['/logbook/spray-records', outcome.alreadyLinked, 'view']);
        return;
      }
      this.preview.set(outcome.draft);
      this.previewIntakeId.set(flight.intakeId);
    });
  }

  cancelPreview(): void {
    this.previewIntakeId.set(null);
    this.preview.set(null);
  }

  /** Starts a draft spray record from the flight (the aircraft and DJI's measurements filled in) and opens it. */
  async createSprayRecord(flight: { intakeId: string }): Promise<void> {
    await this.runAction(flight.intakeId, async () => {
      const { sprayRecordId, alreadyLinked } = await this.dji.createSprayRecordFromFlight(flight.intakeId);
      this.cancelPreview();
      // A flight that already has a record (made elsewhere meanwhile) is shown, not opened for editing.
      await this.router.navigate(['/logbook/spray-records', sprayRecordId, alreadyLinked ? 'view' : 'edit']);
    });
  }

  async dismiss(flight: { intakeId: string }): Promise<void> {
    await this.runAction(flight.intakeId, async () => {
      await this.dji.dismissFlight(flight.intakeId);
      this.cancelPreview();
      await this.load();
    });
  }

  async restore(flight: { intakeId: string }): Promise<void> {
    await this.runAction(flight.intakeId, async () => {
      await this.dji.restoreFlight(flight.intakeId);
      await this.load();
    });
  }

  private async runAction(intakeId: string, action: () => Promise<void>): Promise<void> {
    if (this.busyIntakeId()) return;
    this.busyIntakeId.set(intakeId);
    this.actionError.set(null);
    try {
      await action();
    } catch (error) {
      this.actionError.set(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    } finally {
      this.busyIntakeId.set(null);
    }
  }

  toggleGuide(): void {
    this.showGuide.update((open) => !open);
  }

  private async load(): Promise<void> {
    const seq = ++this.loadSeq;
    const hadStatus = this.status() !== null;
    if (!hadStatus) {
      this.loading.set(true);
    }
    this.failed.set(false);
    try {
      const status = await this.dji.getDroneStatus(this.droneId);
      if (seq !== this.loadSeq) return;
      this.status.set(status);
    } catch {
      if (seq !== this.loadSeq) return;
      if (hadStatus) {
        // A refresh after an action failed: keep what is shown rather than hiding the whole card.
        this.actionError.set("Couldn't refresh the flight list, so what you see may be out of date. Reload the page to check.");
      } else {
        this.status.set(null);
        this.failed.set(true);
      }
    } finally {
      if (seq === this.loadSeq) {
        this.loading.set(false);
      }
    }
  }
}
